import { Fragment, Component, useState, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import axios from 'axios';
import { Grid, Segment, Loader, Icon } from 'semantic-ui-react';
import { CustomSegment } from '../../../../components';

import {
  getEvaluationPlans,
  getProjectOutcomeSummary,
} from '../../../../store/actions';

import { get, map, compact, isEmpty, groupBy } from '../../../../utils/lodash';
import { ContentAndHeaderEvaluation } from '../../../../utils';
import { MeasureEvaluation } from './measureEvaluation';
import { ActivityOutcomeRatings } from '../../../../components/charts/ActivityOutcomeRatings';

import '../activity.scss';

// since summary graph isnt associated with an eval plan id ...add key to use "Collapse all"/"Expand all" with other plans.
const OUTCOME_SUMMARY_KEY = 'outcome-summary';

const CollapsibleCardHeader = ({ collapsed, onToggleCollapse, children }) => (
  <div
    className="report-eval-plan-header"
    role="button"
    tabIndex={0}
    onClick={onToggleCollapse}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggleCollapse();
      }
    }}
  >
    <Icon name={collapsed ? 'angle right' : 'angle down'} />
    <label className="evaluation-heading">{children}</label>
  </div>
);

// reportCharts.outcomeCharts is dependant on whats toggled on/off on the evaluation plan page.
const OutcomeSummaryCharts = ({
  currentActivity,
  outComeChart,
  visibleModes,
  initialFilterState,
  prefetchedData,
}) => {
  return (
    <Segment basic>
      <ActivityOutcomeRatings
        activityId={currentActivity?.id}
        outcomeId={outComeChart?.outcomeId}
        showIncludeToggle={false}
        visibleModes={visibleModes}
        reportMode
        initialFilterState={initialFilterState}
        prefetchedData={prefetchedData}
      />
    </Segment>
  );
};

const OutcomeSummary = (props) => {
  const {
    t,
    haveCharts,
    currentActivity,
    filterStateByOutcomeId = {},
    outcomeSummaryDataMap = {},
    collapsed,
    onToggleCollapse,
  } = props;

  const outcomeEntries = compact(
    map(haveCharts, (charts, title) => {
      const [firstChart] = charts;
      const visibleModes = [
        ...new Set(
          compact(
            map(charts, (chart) => {
              const mode = String(chart.type || '').toLowerCase();
              return mode === 'trend' || mode === 'individual' ? mode : null;
            })
          )
        ),
      ];
      return visibleModes.length > 0
        ? { title, firstChart, visibleModes }
        : null;
    })
  );

  if (isEmpty(outcomeEntries)) return null;

  return (
    <div className="report-eval-plan-top-border">
      <CollapsibleCardHeader
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
      >
        {t('activity.outcome_summary.heading')}
      </CollapsibleCardHeader>
      {!collapsed &&
        map(outcomeEntries, ({ title, firstChart, visibleModes }) => (
          <Fragment key={title}>
            <div className="graph-div outcome-summary-graph">
              <OutcomeSummaryCharts
                currentActivity={currentActivity}
                outComeChart={firstChart}
                visibleModes={visibleModes}
                initialFilterState={
                  filterStateByOutcomeId[firstChart.outcomeId] ?? null
                }
                prefetchedData={
                  outcomeSummaryDataMap[firstChart.outcomeId] ?? null
                }
              />
            </div>
          </Fragment>
        ))}
    </div>
  );
};

const ParticipantGroupLabel = ({ t, group }) => {
  const name = get(group, 'targetGroup.name', '-');
  // participants for a eval plan can either come from the activity type groups (e.g active participants,
  // paid participants ect..), sub groups, or subgroups that vary across a multistage evaluation series
  const count =
    get(group, 'participantCount', null) ??
    get(group, 'targetGroup.actualSize', null) ??
    get(group, 'targetGroup.size', null);
  if (count == null) return name;
  return (
    <>
      {name}{' '}
      <span className="participant-group-count">
        - {t('common.people', { count: Number(count) })}
      </span>
    </>
  );
};

const EvaluationPlan = withTranslation()((props) => {
  const { t, collapsed, onToggleCollapse, outcomeRatingsMap } = props;
  const evalPlan = get(props, 'data', {});
  const selfAssessed = get(evalPlan, 'method.response', '') === 'Self-assessed';

  // TODO:i18n -> lookup should be based on a tag, not a label!!
  const evaluationResponse =
    get(evalPlan, 'method.response', '-') === t('survey.self_assessed')
      ? t('survey.mgr_assessed')
      : get(evalPlan, 'method.response', '-');

  const evaluationQuestions = get(props, 'data.evaluationQuestions', []);
  const reportableQuestions = evaluationQuestions.filter((q) =>
    selfAssessed
      ? q.inReport
      : q.trendInReport !== false || q.individualInReport !== false
  );
  const participantGroups = [...get(evalPlan, 'participantGroups', [])].sort(
    (a, b) =>
      get(a, 'targetGroup.name', '').localeCompare(
        get(b, 'targetGroup.name', '')
      )
  );
  const [hasDataByIndex, setHasDataByIndex] = useState({});
  const handleHasData = useCallback((i, hasData) => {
    setHasDataByIndex((prev) =>
      prev[i] === hasData ? prev : { ...prev, [i]: hasData }
    );
  }, []);
  const allReported =
    reportableQuestions.length > 0 &&
    reportableQuestions.every((_, i) => hasDataByIndex[i] !== undefined);
  const planHasNoData =
    allReported &&
    reportableQuestions.every((_, i) => hasDataByIndex[i] === false);

  // Nothing left to show for this plan at all ... aka every objective excluded via its chart toggles, or self-assessed with its own toggle off
  if (reportableQuestions.length === 0) return null;

  return (
    <Fragment>
      <div className="report-eval-plan-top-border">
        <CollapsibleCardHeader
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
        >
          {get(props, 'data.name', '-')}
          {get(evalPlan, 'seriesSubtitle', null) && (
            <span className="evaluation-heading-subtitle">
              {' '}
              — {evalPlan.seriesSubtitle}
            </span>
          )}
        </CollapsibleCardHeader>
        {!collapsed && (
          <>
            <Grid className="report-eval-plan-stats-grid">
              <Grid.Row>
                <Grid.Column tablet={5} computer={5} mobile={5}>
                  <ContentAndHeaderEvaluation
                    title={
                      get(evalPlan, 'evaluationQuestions', []).length > 1
                        ? t('common.objectives')
                        : t('common.objective')
                    }
                  >
                    {get(evalPlan, 'evaluationQuestions', []).length > 1
                      ? map(
                          get(evalPlan, 'evaluationQuestions', []),
                          (evaluation, i) => (
                            <span
                              className="objective-list-actual-outcome"
                              key={i}
                            >
                              {get(evaluation, 'outcome.title', '-')}
                            </span>
                          )
                        )
                      : map(
                          get(evalPlan, 'evaluationQuestions', []),
                          (evaluation, i) => (
                            <span
                              className="objective-non-list-actual-outcome"
                              key={i}
                            >
                              {get(evaluation, 'outcome.title', '-')}
                            </span>
                          )
                        )}
                  </ContentAndHeaderEvaluation>
                </Grid.Column>
                <Grid.Column tablet={5} computer={5} mobile={5}>
                  <ContentAndHeaderEvaluation
                    title={
                      participantGroups.length > 1
                        ? t('common.evaluation_groups')
                        : t('common.evaluation_group')
                    }
                  >
                    {participantGroups.length > 1
                      ? map(participantGroups, (evaluation, i) => (
                          <span
                            className="objective-list-actual-outcome"
                            key={i}
                          >
                            <ParticipantGroupLabel t={t} group={evaluation} />
                          </span>
                        ))
                      : map(participantGroups, (evaluation, i) => (
                          <span
                            className="objective-non-list-actual-outcome"
                            key={i}
                          >
                            <ParticipantGroupLabel t={t} group={evaluation} />
                          </span>
                        ))}
                  </ContentAndHeaderEvaluation>
                </Grid.Column>
                <Grid.Column tablet={5} computer={5} mobile={5}>
                  <ContentAndHeaderEvaluation
                    title={t('activity.evaluation.evaluation_information')}
                  >
                    {selfAssessed ? (
                      <span className="objective-non-list-actual-outcome">
                        <span>{evaluationResponse}</span>
                      </span>
                    ) : (
                      <span className="objective-non-list-actual-outcome">
                        <span>{`${get(evalPlan, 'method.method', '-')} - ${get(
                          evalPlan,
                          'method.response',
                          '-'
                        )}`}</span>
                      </span>
                    )}
                  </ContentAndHeaderEvaluation>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            {/* <br /> */}
            {map(reportableQuestions, (evaluation, i) => (
              <MeasureEvaluation
                key={i}
                evaluation={evaluation}
                selfAssessed={selfAssessed}
                onHasData={(hasData) => handleHasData(i, hasData)}
                prefetchedData={
                  outcomeRatingsMap?.[
                    `${evaluation.outcomeId}:${evaluation.evaluationPlanId}`
                  ] ?? null
                }
              />
            ))}
            {planHasNoData && (
              <div className="method-heading-box report-plan-no-data-box">
                {t('activity.actual_outcomes.no_responses')}
              </div>
            )}
          </>
        )}
      </div>
    </Fragment>
  );
});

const EvaluationPlans = ({
  evalPlans,
  collapsedPlanIds,
  onToggleCollapse,
  outcomeRatingsMap,
}) => {
  if (isEmpty(evalPlans)) return null;
  return (
    <>
      {map(
        evalPlans,
        (plan, i) =>
          !get(plan, 'isDisabled', false) && (
            <EvaluationPlan
              key={i}
              data={plan}
              collapsed={collapsedPlanIds?.has(plan.id)}
              onToggleCollapse={() => onToggleCollapse(plan.id)}
              outcomeRatingsMap={outcomeRatingsMap}
            />
          )
      )}
    </>
  );
};

class ActualOutcomes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      haveCharts: {},
      filterStateByOutcomeId: {},
      evalPlans: [],
      collapsedPlanIds: new Set(),
      outcomeRatingsMap: null,
      outcomeSummaryDataMap: {},
    };
  }

  // All collapsible cards = Outcome Summary graph + Each Eval Plan
  getCollapsibleKeys() {
    const { evalPlans, haveCharts } = this.state;
    return [
      ...(isEmpty(haveCharts) ? [] : [OUTCOME_SUMMARY_KEY]),
      ...evalPlans.map((plan) => plan.id),
    ];
  }

  // All sections default to expanded (unset) so the page reads as a report, with collapsible cards to help with navigation
  toggleCollapseAll = () => {
    const { collapsedPlanIds } = this.state;
    const keys = this.getCollapsibleKeys();
    const allCollapsed =
      keys.length > 0 && keys.every((key) => collapsedPlanIds.has(key));
    this.setState({
      collapsedPlanIds: allCollapsed ? new Set() : new Set(keys),
    });
  };

  togglePlanCollapse = (planId) => {
    this.setState((prev) => {
      const next = new Set(prev.collapsedPlanIds);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return { collapsedPlanIds: next };
    });
  };

  prefetchOutcomeRatings(evalPlans) {
    const activityId = get(this.props, 'currentActivity.id', null);
    const pairs = [];
    (evalPlans || []).forEach((plan) => {
      (plan.evaluationQuestions || []).forEach((evaluation) => {
        if (!evaluation.inReport) return;
        if (!evaluation.outcomeId || !evaluation.evaluationPlanId) return;
        pairs.push({
          outcomeId: evaluation.outcomeId,
          evaluationPlanId: evaluation.evaluationPlanId,
        });
      });
    });

    if (!activityId || pairs.length === 0) {
      this.setState({ outcomeRatingsMap: {} });
      return;
    }

    Promise.allSettled(
      pairs.map(({ outcomeId, evaluationPlanId }) =>
        axios
          .get(`/project/${activityId}/outcome-ratings`, {
            params: { outcomeId, evaluationPlanIds: evaluationPlanId },
          })
          .then((res) => ({
            key: `${outcomeId}:${evaluationPlanId}`,
            data: res.data,
          }))
      )
    ).then((results) => {
      const outcomeRatingsMap = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { key, data } = result.value;
          if (data?.ok) outcomeRatingsMap[key] = data;
        }
      });
      this.setState({ outcomeRatingsMap });
    });
  }

  // Unfiltered (all plans) fetch per outcome, one per Outcome Summary card. ActivityOutcomeRatings does filtering down to match selection on eval page
  prefetchOutcomeSummaryRatings(outcomeIds) {
    const activityId = get(this.props, 'currentActivity.id', null);
    if (!activityId || outcomeIds.length === 0) {
      this.setState({ outcomeSummaryDataMap: {} });
      return;
    }

    Promise.allSettled(
      outcomeIds.map((outcomeId) =>
        axios
          .get(`/project/${activityId}/outcome-ratings`, {
            params: { outcomeId },
          })
          .then((res) => ({ outcomeId, data: res.data }))
      )
    ).then((results) => {
      const outcomeSummaryDataMap = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { outcomeId, data } = result.value;
          if (data?.ok) outcomeSummaryDataMap[outcomeId] = data;
        }
      });
      this.setState({ outcomeSummaryDataMap });
    });
  }

  async componentDidMount() {
    const {
      getEvaluationPlans,
      getProjectOutcomeSummary,
      currentActivity,
      getAllImages,
    } = this.props;
    await getAllImages({ getAllGraphImages: true, reportLoaded: true });
    await getEvaluationPlans(currentActivity.id);
    getProjectOutcomeSummary(currentActivity.id);
    await this.setData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentActivity !== this.props.currentActivity) {
      this.setData();
    }
  }

  async setData() {
    const allOutcomeCharts = get(
      this.props,
      'currentActivity.reportCharts.outcomeCharts',
      []
    );

    const filterStateByOutcomeId = {};
    allOutcomeCharts.forEach((chart) => {
      if (chart.type === 'FILTER' && chart.outcomeId != null) {
        filterStateByOutcomeId[chart.outcomeId] = chart.filterState || null;
      }
    });

    const haveCharts = groupBy(
      allOutcomeCharts.filter((chart) => chart.type !== 'FILTER'),
      (chart) => chart.outcome.title
    );

    // change the order of chart type
    for (const outcomeTitle in haveCharts) {
      haveCharts[outcomeTitle].sort((a, b) => {
        const priorityMap = {
          AGGREGATE: 1,
          TREND: 1,
          SEPERATE: 2,
          INDIVIDUAL: 2,
          INTERCEPT_AGGREGATE: 3,
          INTERCEPT_SEPERATE: 3,
        };
        return priorityMap[a.type] - priorityMap[b.type];
      });
    }

    const evalPlans = compact(
      map(get(this.props, 'currentActivity.evaluationPlans', []), (plan) => {
        const evaluationQuestions = compact(
          map(plan.evaluationQuestions, (evaluation) => {
            if (plan.isDisabled && !evaluation.haveResponses) {
              return;
            }

            return evaluation;
          })
        );

        if (!isEmpty(evaluationQuestions)) {
          return {
            ...plan,
            evaluationQuestions,
          };
        }
      })
    );

    if (isEmpty(haveCharts)) {
      await this.props.getAllImages({
        getAllGraphImages: false,
        reportLoaded: false,
      });
    } else {
      await this.props.getAllImages({
        getAllGraphImages: false,
        reportLoaded: true,
      });
    }

    // *On Reports Tab* -> If less than 2 eval plans show everything expanded, but have ability to collapse to help with navigation. Also Summary always shown expanded.
    const collapsedPlanIds =
      evalPlans.length > 2
        ? new Set(evalPlans.map((plan) => plan.id))
        : new Set();

    this.setState({
      haveCharts,
      filterStateByOutcomeId,
      evalPlans,
      collapsedPlanIds,
    });
    this.prefetchOutcomeRatings(evalPlans);
    const outcomeIdsForSummary = compact(
      Object.values(haveCharts).map((charts) => charts[0]?.outcomeId)
    );
    this.prefetchOutcomeSummaryRatings(outcomeIdsForSummary);
  }

  render() {
    const { t, currentActivity, evalDetail, projectLoading } = this.props;
    const {
      haveCharts,
      filterStateByOutcomeId,
      outcomeSummaryDataMap,
      evalPlans,
      collapsedPlanIds,
      outcomeRatingsMap,
    } = this.state;

    if (isEmpty(currentActivity)) return null;

    const hasSinglePlan =
      get(currentActivity, 'evaluationPlans', []).length === 1;
    const ratingsReady = outcomeRatingsMap !== null;
    const collapsibleKeys = this.getCollapsibleKeys();
    const allCollapsed =
      collapsibleKeys.length > 0 &&
      collapsibleKeys.every((key) => collapsedPlanIds.has(key));

    return currentActivity.evaluationPlans &&
      currentActivity.evaluationPlans.length ? (
      <CustomSegment
        title={t('activity.actual_outcomes.title')}
        className="actual-outcomes"
        children={
          <Segment className="actual-outcomes-seg">
            <div className="outcomes-container">
              <div className="chart-container">
                {isEmpty(haveCharts) || hasSinglePlan ? null : (
                  <OutcomeSummary
                    //evalPlan true to show evaluation plan count in report header
                    evalPlan={true}
                    haveCharts={haveCharts}
                    filterStateByOutcomeId={filterStateByOutcomeId}
                    outcomeSummaryDataMap={outcomeSummaryDataMap}
                    collapsed={collapsedPlanIds.has(OUTCOME_SUMMARY_KEY)}
                    onToggleCollapse={() =>
                      this.togglePlanCollapse(OUTCOME_SUMMARY_KEY)
                    }
                    {...this.props}
                    singleReport={true}
                  />
                )}
                {isEmpty(evalPlans) ? null : !ratingsReady ? (
                  <div className="outcome-summary-loading">
                    <Loader active inline="centered" />
                  </div>
                ) : (
                  <EvaluationPlans
                    evalDetail={evalDetail}
                    evalPlans={evalPlans}
                    collapsedPlanIds={collapsedPlanIds}
                    onToggleCollapse={this.togglePlanCollapse}
                    outcomeRatingsMap={outcomeRatingsMap}
                    {...this.props}
                  />
                )}
              </div>
            </div>
          </Segment>
        }
        rightHeader={
          <div className="actual-outcomes-header-actions">
            {collapsibleKeys.length > 1 && (
              <button
                type="button"
                className="actual-outcomes-collapse-all-btn"
                onClick={this.toggleCollapseAll}
              >
                {allCollapsed
                  ? t('common.expand_all')
                  : t('common.collapse_all')}
              </button>
            )}
            <Loader active={projectLoading} inline />
          </div>
        }
        extraMargin
        toggle={false}
      />
    ) : null;
  }
}

const mapStateToProps = (state) => {
  return {
    ...state.activity,
    projectLoading: state.report.projectLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getProjectOutcomeSummary,
      getEvaluationPlans,
    },
    dispatch
  );
};

ActualOutcomes = connect(mapStateToProps, mapDispatchToProps)(ActualOutcomes);
ActualOutcomes = withTranslation()(ActualOutcomes);
export { ActualOutcomes };
